import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Menu, // ÚJ: Menu komponens
  ListItemIcon, // ÚJ: ListItemIcon
  ListItemText, // ÚJ: ListItemText
  IconButton
} from '@mui/material';
import { MoreVert, CheckCircle, Cancel, Payment } from '@mui/icons-material'; // ÚJ: Ikonok
import { format, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import commissionService from '../../services/commissionService';
import { useNavigate } from 'react-router-dom';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { formatHuf } from '../../utils/currency';

// Oldalsávos asztali nézetben ezek a másodlagos oszlopok csak széles képernyőn látszanak
const hideBelowXl = { display: { xs: 'none', xl: 'table-cell' } };

const CommissionedBookingsPage = ({ page, limit, searchTerm, onPageChange, pagination }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('ALL');
  const [actionLoading, setActionLoading] = useState(false); // ÚJ: Loading state az akciókhoz

  // ÚJ: Menu state-ek
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBookingForAction, setSelectedBookingForAction] = useState(null);
  const openMenu = Boolean(anchorEl);

  const fetchCommissionedBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        search: searchTerm,
        payoutStatus: payoutStatusFilter === 'ALL' ? undefined : payoutStatusFilter,
      };
      const response = await commissionService.getCommissionedBookings(page, limit, filters);
      if (response.success) {
        setBookings(response.data);
      } else {
        setError(response.message || 'Jutalékos foglalások betöltése sikertelen.');
      }
    } catch (err) {
      console.error('Error fetching commissioned bookings:', err);
      setError('Hiba történt a jutalékos foglalások betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, payoutStatusFilter]);

  useEffect(() => {
    fetchCommissionedBookings();
  }, [fetchCommissionedBookings]);

  const getPayoutStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'PAID': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getPayoutStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Függőben';
      case 'PAID': return 'Kifizetve';
      case 'CANCELLED': return 'Törölve';
      default: return 'Ismeretlen';
    }
  };

  // ÚJ: Menu kezelő függvények
  const handleMenuClick = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBookingForAction(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBookingForAction(null);
  };

  // ÚJ: Kifizetési státusz frissítése
  const handleUpdatePayoutStatus = async (bookingId, newStatus) => {
    setActionLoading(true);
    handleMenuClose(); // Bezárjuk a menüt
    try {
      const response = await commissionService.updateCommissionPayoutStatus(bookingId, newStatus); // ÚJ service metódus
      if (response.success) {
        // Frissítjük a listát
        fetchCommissionedBookings();
        // Itt lehetne egy snackbar üzenet is
      } else {
        setError(response.message || 'Hiba a kifizetési státusz frissítése során.');
      }
    } catch (err) {
      console.error('Error updating payout status:', err);
      setError('Hiba a kifizetési státusz frissítése során.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || actionLoading) { // MÓDOSÍTVA: actionLoading is figyelembe véve
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="payout-status-select-label">Kifizetési státusz</InputLabel>
            <Select
              labelId="payout-status-select-label"
              value={payoutStatusFilter}
              label="Kifizetési státusz"
              onChange={(e) => setPayoutStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">Összes</MenuItem>
              <MenuItem value="PENDING">Függőben</MenuItem>
              <MenuItem value="PAID">Kifizetve</MenuItem>
              <MenuItem value="CANCELLED">Törölve</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <ResponsiveTableContainer stickyLastColumn>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Foglalás ID</TableCell>
              <TableCell sx={hideBelowXl}>Esemény típusa</TableCell>
              <TableCell>Szolgáltató</TableCell>
              <TableCell align="right">Jutalék összege</TableCell>
              <TableCell>Kifizetési státusz</TableCell>
              <TableCell sx={hideBelowXl}>Előleg hat.</TableCell>
              <TableCell>Teljes hat.</TableCell>
              <TableCell>Jutalék esedékes</TableCell>
              <TableCell>Megerősítések</TableCell>
              <TableCell align="center">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Nincsenek jutalékos foglalások.
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => {
                const now = new Date();
                const fullDeadline = booking.fullPaymentDeadline ? new Date(booking.fullPaymentDeadline) : null;
                const dueDate = booking.commissionDueDate ? new Date(booking.commissionDueDate) : null;
                const isOverdue = fullDeadline && fullDeadline < now && booking.commissionPayoutStatus === 'PENDING';
                return (
                <TableRow key={booking.id} sx={isOverdue ? { bgcolor: 'rgba(211,47,47,0.06)' } : {}}>
                  <TableCell>{booking.id}</TableCell>
                  <TableCell sx={hideBelowXl}>{booking.eventType}</TableCell>
                  <TableCell>{booking.provider?.businessName || 'N/A'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatHuf(booking.commissionAmount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getPayoutStatusText(booking.commissionPayoutStatus)}
                      color={getPayoutStatusColor(booking.commissionPayoutStatus)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={hideBelowXl}>
                    {booking.depositDeadline
                      ? format(new Date(booking.depositDeadline), 'MM.dd.', { locale: hu })
                      : '–'}
                  </TableCell>
                  <TableCell>
                    {fullDeadline ? (
                      <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'} fontWeight={isOverdue ? 700 : 400}>
                        {format(fullDeadline, 'MM.dd.', { locale: hu })}
                        {isOverdue && ' ⚠️'}
                      </Typography>
                    ) : '–'}
                  </TableCell>
                  <TableCell>
                    {dueDate ? (
                      <Typography variant="body2" color={dueDate < now && booking.commissionPayoutStatus === 'PENDING' ? 'error.main' : 'text.primary'}
                        fontWeight={dueDate < now && booking.commissionPayoutStatus === 'PENDING' ? 700 : 400}>
                        {format(dueDate, 'yyyy.MM.dd.', { locale: hu })}
                      </Typography>
                    ) : '–'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color={booking.clientPaymentConfirmedAt ? 'success.main' : 'text.disabled'}>
                        {booking.clientPaymentConfirmedAt ? '✓ Ügyfél' : '○ Ügyfél'}
                      </Typography>
                      <Typography variant="caption" color={booking.providerPaymentConfirmedAt ? 'success.main' : 'text.disabled'}>
                        {booking.providerPaymentConfirmedAt ? '✓ Szolg.' : '○ Szolg.'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/foglalasok/${booking.id}`)}
                      sx={{ mr: 1 }}
                    >
                      Részletek
                    </Button>
                    <IconButton
                      aria-label="további opciók"
                      onClick={(event) => handleMenuClick(event, booking)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenu && selectedBookingForAction?.id === booking.id}
                      onClose={handleMenuClose}
                    >
                      {selectedBookingForAction?.commissionPayoutStatus === 'PENDING' && (
                        <MenuItem onClick={() => handleUpdatePayoutStatus(selectedBookingForAction.id, 'PAID')}>
                          <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                          <ListItemText>Kifizetve jelölése</ListItemText>
                        </MenuItem>
                      )}
                      {selectedBookingForAction?.commissionPayoutStatus === 'PENDING' && (
                        <MenuItem onClick={() => handleUpdatePayoutStatus(selectedBookingForAction.id, 'CANCELLED')}>
                          <ListItemIcon><Cancel fontSize="small" /></ListItemIcon>
                          <ListItemText>Törölve jelölése</ListItemText>
                        </MenuItem>
                      )}
                      {/* Itt lehetne egy "Kifizetés indítása Barionon" gomb is, ha a Payout API-t is implementáljuk */}
                      {/* <MenuItem onClick={() => alert('Kifizetés indítása Barionon')}>
                        <ListItemIcon><Payment fontSize="small" /></ListItemIcon>
                        <ListItemText>Kifizetés indítása Barionon</ListItemText>
                      </MenuItem> */}
                    </Menu>
                  </TableCell>
                </TableRow>
              );})
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={onPageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default CommissionedBookingsPage;